import Button from '@material-ui/core/Button';
import Icon from '@material-ui/core/Icon';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/styles';
import classNames from 'classnames';
import React from 'react';
import { Observe } from 'rewire-core';
import { createMultiSelectAutoCompleteEditor, ICell } from 'rewire-grid';
import { CSSTheme, TextEditorProps, Theme } from 'rewire-ui';
import AddReasonDialog from '../schedule/AddReasonDialog';
import hoursEarnedTypeDialogModel from '../schedule/HoursEarnedTypeDialogModel';
import ScheduleRuleDialog from '../schedule/ScheduleRuleDialog';
import Grid from '../ui/Grid';
import Loader from '../ui/Loader';
import OptionsToggleMenu from '../ui/OptionsToggleMenu';
import { getArrayString } from '../utility/Searchers';
import AddHoursEarnedTypeDialog from './AddHoursEarnedTypeDialog';
import RuleSettingsEditorModel from './RuleSettingsEditorModel';
import TimecardExportEditorView from './TimecardExportOutputEditorView';

const ruleEditorStyle = (theme: Theme) =>
  ({
    root: {
      display: 'flex',
      // flexDirection: 'column',
      minHeight: 394,
      flex: '1',
      '& td.result': {
        borderLeft: `2px solid ${theme.palette.error.dark}`,
      },
    },
    menuItem: {
      minWidth: '300px',
    },
    menuButton: {
      minWidth: '0px',
      minHeight: '0px',
      padding: '0px',
      margin: 0,
      // marginLeft: '8px',
    },
    addButton: {
      textTransform: 'none',
    },
    left: {
      flex: '0 0 250px',
      margin: '0 8px 0 0',
      border: `1px solid ${theme.palette.grey['300']}`,
    },
    title: {
      display: 'flex',
      alignSelf: 'stretch',
      alignItems: 'center',
      '& button': { marginLeft: 8 },
      '& h6': { flex: 1 },
    },
    header: {
      display: 'flex',
      flexShrink: 0,
      alignItems: 'center',
      flexDirection: 'column',
      margin: '0px 0px 8px 0px',
    },
    right: {
      flex: '1',
      minWidth: '250px',
      flexDirection: 'column',
      display: 'flex',
    },
  }) as CSSTheme;

export const useRuleEditorStyle = makeStyles(ruleEditorStyle);

export interface IToolbarUndo {
  canUndo: () => boolean;
  undo: () => void;
}
export const ToolbarUndoButton = ({ settings }: { settings: IToolbarUndo }) => {
  return (
    <Observe>
      {() => (
        <Button className="span1" variant="contained" disabled={!settings.canUndo()} onClick={() => settings.undo()}>
          <Icon className="undo">{'undo'}</Icon>
          Undo
        </Button>
      )}
    </Observe>
  );
};

export interface IToolbarSave {
  canSave: () => boolean | undefined;
  save: () => Promise<boolean | undefined>;
}
export const ToolbarSaveButton = ({ settings, style }: { settings: IToolbarSave; style?: React.CSSProperties }) => {
  return (
    <Observe>
      {() => (
        <Button
          className="span1"
          color="primary"
          variant="contained"
          style={{ ...style }}
          disabled={!settings.canSave()}
          onClick={() => settings.save()}
        >
          <Icon className="save">{'save'}</Icon>
          Save
        </Button>
      )}
    </Observe>
  );
};

export const ToolbarButtons = ({ settings, children }: { settings: IToolbarSave & IToolbarUndo; children?: React.ReactChild }) => {
  return (
    <>
      {children}
      <ToolbarUndoButton settings={settings} />
      <ToolbarSaveButton settings={settings} />
    </>
  );
};

export const RuleSettingsEditor = React.memo(({ settings }: { settings: RuleSettingsEditorModel }) => {
  const classes = useRuleEditorStyle();
  const Buttons = settings.ToolbarButtons ?? ToolbarButtons;
  return (
    <Observe>
      {() => (
        <>
          <div className={classes.header}>
            <div className={classes.title}>
              <Typography variant="h6">{settings.titleText}</Typography>
              <Observe
                render={() => (
                  <OptionsToggleMenu
                    tooltip="Add UDFs Column"
                    className="span1"
                    buttonProps={{
                      variant: 'contained',
                      className: classNames('span1', classes.addButton),
                      disabled: settings.availableUdfs.length === 0 || settings.viewOnly,
                    }}
                    classes={{ menuButton: classes.menuButton, menuItem: classes.menuItem }}
                    buttonContent={
                      <>
                        <Icon className="add">{'add'}</Icon>
                        Add UDFs Column
                      </>
                    }
                    onItemClick={(item) => settings.toggleUdfColumn(item.name)}
                    items={settings.availableUdfs}
                  />
                )}
              />
              <Buttons settings={settings} />
            </div>
          </div>
          <div className={classes.root}>
            <div className={classes.left}>
              <List component="nav">
                {settings.tables.map((table) => (
                  <ListItem selected={table.selected} key={table.id} button onClick={() => settings.selectItem(table)}>
                    <ListItemText primary={table.name} />
                  </ListItem>
                ))}
              </List>
            </div>
            <div className={classes.right}>
              <Observe>
                {() => (
                  <Loader loading={settings.isLoading}>
                    <Observe>{() => <Grid grid={settings.grid} style={{ height: '100%' }} />}</Observe>
                  </Loader>
                )}
              </Observe>
            </div>
          </div>
          <AddHoursEarnedTypeDialog model={hoursEarnedTypeDialogModel} />
          <AddReasonDialog />
          <ScheduleRuleDialog />
          <TimecardExportEditorView />
        </>
      )}
    </Observe>
  );
});

export function createEditor(options: any) {
  const Editor: any = createMultiSelectAutoCompleteEditor(options);
  return (props: TextEditorProps) => {
    props.field.value = Array.isArray(props.field.value) ? props.field.value : [];
    return (
      <Editor
        {...props}
        onValueChange={(value: any) => {
          if (!value || value.length === 0) {
            props.onValueChange('*');
            return;
          }
          props.onValueChange(value);
        }}
      />
    );
  };
}

const empty = '«empty»';
export const MultiSelectAutoCompleteRenderer = (map: (value: any) => any = getArrayString) =>
  React.memo<{ cell: ICell }>(({ cell }) => (
    <Observe>
      {() => {
        if (!cell.value) {
          return <span>{empty}</span>;
        }

        if (cell.value === '*') {
          return <span>*</span>;
        }

        return <span>{map(cell.value)}</span>;
      }}
    </Observe>
  ));
